// API の入出力の型。Web・サーバー・アプリで共有する（仕様書「API」）。
// 名前・連絡先・住所・メモ・点呼の中身は API を通らず、画面が Firestore に直接読み書きする（firestore.ts）
import { z } from 'zod';

// 文言を指定していない入力エラーも日本語で返す
z.config(z.locales.ja());
import { RULES, ROLES } from './rules';

const name = z.string().trim().min(1, '必須です').max(RULES.nameMax, `${RULES.nameMax} 文字までです`);
const email = z.string().trim().toLowerCase().email('メールアドレスの形が正しくありません');
const hhmm = z.string().regex(/^([01]?\d|2[0-3]):[0-5]\d$/, '「14:00」の形で入れてください');

export const AccountType = z.enum(['workspace', 'personal', 'apple', 'email']);

export const DriverCreate = z.strictObject({
  name,
  phone: z.string().trim().max(20).optional(),
  email,
  accountType: AccountType,
  vehicleId: z.string().optional(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
});
export type DriverCreate = z.infer<typeof DriverCreate>;
/** API に送る部分（名前・電話・メールは Firestore の drivers） */
export const DriverRegister = DriverCreate.pick({ accountType: true, vehicleId: true, color: true });
export type DriverRegister = z.infer<typeof DriverRegister>;

export const Driver = z.object({
  id: z.string(),
  accountType: AccountType,
  color: z.string(),
  status: z.enum(['invited', 'pending', 'active', 'retired']),
  retiredAt: z.string().nullable(),
  vehicleId: z.string().nullable(),
  /** 版番号（更新のときに If-Match で送る） */
  version: z.number(),
});
export type Driver = z.infer<typeof Driver>;

export const StaffCreate = z.strictObject({ name, email, role: z.enum(ROLES) });
export type StaffCreate = z.infer<typeof StaffCreate>;
export const StaffRegister = StaffCreate.pick({ role: true });
export type StaffRegister = z.infer<typeof StaffRegister>;

export const PlaceCreate = z.strictObject({
  name,
  address: z.string().trim().min(1).max(200),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  phone: z.string().trim().max(20).optional(),
  memo: z.string().trim().max(RULES.memoMax).optional(),
  isFavorite: z.boolean().default(true),
  loadMin: z.number().int().min(0).max(240).default(RULES.defaultWorkMin),
  unloadMin: z.number().int().min(0).max(240).default(RULES.defaultWorkMin),
});
export type PlaceCreate = z.infer<typeof PlaceCreate>;

/** API に送る部分（名前・住所・電話・メモは Firestore の places） */
export const PlaceRegister = PlaceCreate.pick({ lat: true, lng: true, isFavorite: true, loadMin: true, unloadMin: true });
export type PlaceRegister = z.infer<typeof PlaceRegister>;
export const Place = z.object({ id: z.string(), lat: z.number(), lng: z.number(), isFavorite: z.boolean(), loadMin: z.number(), unloadMin: z.number(), version: z.number() });
export type Place = z.infer<typeof Place>;

export const StopInput = z.strictObject({
  kind: z.enum(['pickup', 'drop']),
  placeId: z.string().min(1),
  dueAt: hhmm.optional(),
  workMin: z.number().int().min(0).max(240).optional(),
  /** 届け先だけ：どのピックアップ先の荷物か（行き先の並びの番号）。なければ pickupsOf の決まり */
  from: z.array(z.number().int().min(0)).max(12).optional(),
});
export type StopInput = z.infer<typeof StopInput>;

/** 届け先ごとの積む場所。指定がなければ、前の届け先より後のピックアップ先、それがなければそれまでのすべて */
export function pickupsOf(stops: Pick<StopInput, 'kind' | 'from'>[]): Map<number, number[]> {
  const out = new Map<number, number[]>();
  let since = 0;
  stops.forEach((s, i) => {
    if (s.kind !== 'drop') return;
    const before = (from: number) => stops.map((x, k) => ({ x, k })).filter(({ x, k }) => x.kind === 'pickup' && k >= from && k < i).map(({ k }) => k);
    const recent = before(since);
    out.set(i, s.from ?? (recent.length ? recent : before(0)));
    since = i + 1;
  });
  return out;
}

/** 行き先の並びの決まり：ピックアップ先とドロップ先が要る。最初のドロップより前にピックアップ。届け先は 4 か所まで */
function checkStops(stops: StopInput[], ctx: z.RefinementCtx) {
  const drops = stops.filter((s) => s.kind === 'drop').length;
  const firstPickup = stops.findIndex((s) => s.kind === 'pickup');
  const firstDrop = stops.findIndex((s) => s.kind === 'drop');
  if (firstPickup < 0) ctx.addIssue({ code: 'custom', path: ['stops'], message: 'ピックアップ先が必要です' });
  if (firstDrop < 0) ctx.addIssue({ code: 'custom', path: ['stops'], message: 'ドロップ先が必要です' });
  if (firstPickup >= 0 && firstDrop >= 0 && firstDrop < firstPickup)
    ctx.addIssue({ code: 'custom', path: ['stops'], message: '最初のドロップより前にピックアップを置いてください' });
  if (firstPickup >= 0 && firstDrop >= 0) {
    const map = pickupsOf(stops);
    if ([...map].some(([i, from]) => !from.length || from.some((k) => k >= i || stops[k]?.kind !== 'pickup')))
      ctx.addIssue({ code: 'custom', path: ['stops'], message: '届け先の荷物は、その前のピックアップ先から選んでください' });
    const used = new Set([...map.values()].flat());
    if (stops.some((s, i) => s.kind === 'pickup' && !used.has(i)))
      ctx.addIssue({ code: 'custom', path: ['stops'], message: 'どの届け先にも届けないピックアップ先があります' });
  }
  if (drops > RULES.maxDropsPerBatch)
    ctx.addIssue({ code: 'custom', path: ['stops'], message: `届け先は ${RULES.maxDropsPerBatch} か所（配送 ${RULES.maxDropsPerBatch} 件）までです` });
}
const stops = z.array(StopInput).max(RULES.maxStopsPerBatch, `行き先は ${RULES.maxStopsPerBatch} か所までです`);

/** 配送指示。行き先は回る順に並べる。届け先ごとに配送番号を発行する */
export const BatchCreate = z
  .strictObject({
    driverId: z.string().min(1).optional(), // 空なら「未割り当てで保存」
    clientId: z.string().min(1).optional(),
    stops,
  })
  .superRefine((b, ctx) => checkStops(b.stops, ctx));
export type BatchCreate = z.infer<typeof BatchCreate>;

export const BatchCreated = z.object({
  batchId: z.string(),
  status: z.enum(['draft', 'sent']),
  deliveries: z.array(z.object({ deliveryNo: z.string(), dropPlaceId: z.string() })),
});
export type BatchCreated = z.infer<typeof BatchCreated>;

export const DeliveryStatus = z.enum(['unassigned', 'unaccepted', 'accepted', 'picking', 'picked', 'dropping', 'dropped', 'canceled', 'reassigned']);

export const DeliveryRow = z.object({
  deliveryNo: z.string(),
  batchId: z.string(),
  driverId: z.string().nullable(),
  status: DeliveryStatus,
  pickupPlaceIds: z.array(z.string()),
  dropPlaceId: z.string(),
  dueAt: z.string().nullable(),
  clientId: z.string().nullable(),
  createdAt: z.string(),
});
export type DeliveryRow = z.infer<typeof DeliveryRow>;
/** 配送の検索（仕様書「配送の検索・写真、記録」）。日付は作った日（日本時間） */
export const DeliverySearch = z.strictObject({
  q: z.string().regex(/^[0-9A-Z]{1,6}$/, '配送番号は英数字 6 文字までです').optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日付を「2026-10-29」の形で指定してください').optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日付を「2026-10-29」の形で指定してください').optional(),
  driverId: z.string().max(64).optional(),
  status: z.enum(['open', 'done', 'canceled', 'unassigned']).optional(),
}).refine((v) => !v.from || !v.to || v.from <= v.to, { message: '始まりの日は終わりの日より前にしてください' });
export type DeliverySearch = z.infer<typeof DeliverySearch>;

export const ApiError = z.object({
  error: z.object({ code: z.string(), message: z.string(), detail: z.unknown().optional(), request_id: z.string().optional() }),
});
export type ApiError = z.infer<typeof ApiError>;

// ===== 荷主（仕様書「荷主の欄」） =====
export const ClientCreate = z.strictObject({
  name: z.string().trim().min(1, '必須です').max(RULES.clientNameMax, `${RULES.clientNameMax} 文字までです`),
  showPhotos: z.boolean(),
});
export type ClientCreate = z.infer<typeof ClientCreate>;
export const ClientUpdate = ClientCreate.extend({ active: z.boolean() });
export type ClientUpdate = z.infer<typeof ClientUpdate>;
export const Client = z.object({ id: z.string(), name: z.string(), showPhotos: z.boolean(), active: z.boolean(), version: z.number() });
export type Client = z.infer<typeof Client>;

// ===== 車両・検知器 =====
export const VehicleCreate = z.strictObject({
  plateNumber: z.string().trim().min(1, '必須です').max(30),
  nickname: z.string().trim().min(1, '必須です').max(20, '20 文字までです'),
});
export type VehicleCreate = z.infer<typeof VehicleCreate>;
export const Vehicle = VehicleCreate.extend({ id: z.string(), status: z.enum(['active', 'inactive']), version: z.number() });
export type Vehicle = z.infer<typeof Vehicle>;
/** 直す（無効にすると、配送アプリの点呼で選べなくなる。過去の記録は残る） */
export const VehicleUpdate = VehicleCreate.extend({ status: z.enum(['active', 'inactive']) });
export type VehicleUpdate = z.infer<typeof VehicleUpdate>;

export const DetectorCreate = z.strictObject({
  name: z.string().trim().min(1, '必須です').max(40),
  model: z.string().trim().max(40).optional(),
});
export type DetectorCreate = z.infer<typeof DetectorCreate>;
/** 検知器の状態：使える・故障中・廃棄（故障中と廃棄は点呼で使えない） */
export const DetectorStatus = z.enum(['active', 'broken', 'retired']);
export type DetectorStatus = z.infer<typeof DetectorStatus>;
export const DETECTOR_STATUS_LABEL: Record<DetectorStatus, string> = { active: '使える', broken: '故障中', retired: '廃棄' };
export const Detector = DetectorCreate.extend({ id: z.string(), status: DetectorStatus, model: z.string().nullable(), version: z.number() });
export type Detector = z.infer<typeof Detector>;
export const DetectorUpdate = DetectorCreate.extend({ status: DetectorStatus });
export type DetectorUpdate = z.infer<typeof DetectorUpdate>;

// ===== 点呼・アルコールチェック（仕様書「点呼・アルコールチェックの確認」） =====
export const RollCallStatus = z.enum(['waiting', 'confirmed', 'remeasure', 'rejected', 'not_done']);

/** ドライバーがアプリから送る内容（いまは開発用の入口から入れる）。測定値そのものは Firestore の rollCallDetails */
export const RollCallSubmit = z.strictObject({
  driverId: z.string().min(1),
  vehicleId: z.string().min(1),
  kind: z.enum(['before', 'after']),
  alcohol: z.number().min(0).max(5),
  detectorId: z.string().min(1).optional(),
});
export type RollCallSubmit = z.infer<typeof RollCallSubmit>;

/** 点呼の確認の画面の入力。健康状態のメモ・指示事項は Firestore の rollCallDetails に書く */
export const RollCallConfirmForm = z
  .strictObject({
    decision: z.enum(['confirm', 'remeasure', 'reject']),
    healthOk: z.boolean(),
    healthNote: z.string().trim().max(200).optional(),
    inspectionOk: z.boolean().optional(),
    detectorId: z.string().min(1, '使った検知器を選んでください'),
    detectorChecked: z.boolean(),
    instruction: z.string().trim().max(RULES.memoMax, `${RULES.memoMax} 文字までです`).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'confirm' && !v.detectorChecked)
      ctx.addIssue({ code: 'custom', path: ['detectorChecked'], message: '検知器の動作確認をしてください' });
    if (v.decision === 'confirm' && !v.healthOk && !v.healthNote)
      ctx.addIssue({ code: 'custom', path: ['healthNote'], message: '健康状態に問題があるときは、乗務させる理由を書いてください' });
  });
export type RollCallConfirmForm = z.infer<typeof RollCallConfirmForm>;
/** API に送る部分 */
export const RollCallConfirm = z
  .strictObject({
    decision: z.enum(['confirm', 'remeasure', 'reject']),
    healthOk: z.boolean(),
    inspectionOk: z.boolean().optional(),
    detectorId: z.string().min(1, '使った検知器を選んでください'),
    detectorChecked: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'confirm' && !v.detectorChecked)
      ctx.addIssue({ code: 'custom', path: ['detectorChecked'], message: '検知器の動作確認をしてください' });
  });
export type RollCallConfirm = z.infer<typeof RollCallConfirm>;

export const RollCall = z.object({
  id: z.string(),
  driverId: z.string(),
  vehicle: z.string(),
  kind: z.enum(['before', 'after']),
  at: z.string(),
  /** 乗務前の点呼で、前回の勤務の終わりからの時間（分）。前回がなければ null */
  restMin: z.number().nullable(),
  alcoholDetected: z.boolean(),
  status: RollCallStatus,
  /** 確認したスタッフの ID（名前は Firestore の staff） */
  checkedBy: z.string().nullable(),
  checkedAt: z.string().nullable(),
  detector: z.string().nullable(),
  detectorChecked: z.boolean(),
});
export type RollCall = z.infer<typeof RollCall>;

export const InviteCreate = z.strictObject({ kind: z.enum(['driver', 'staff']), id: z.string().min(1) });
export type InviteCreate = z.infer<typeof InviteCreate>;
export const InviteResult = z.object({ sent: z.boolean() });
export type InviteResult = z.infer<typeof InviteResult>;

// ===== 開発者モード（ステージングと手元だけ。仕様書「開発者モード」） =====
export const DevFlags = z.strictObject({ ignoreRest: z.boolean(), ignoreConsent: z.boolean(), autoApproveDevice: z.boolean(), autoConfirmRollCall: z.boolean() });
export type DevFlags = z.infer<typeof DevFlags>;
export const DEV_FLAGS_OFF: DevFlags = { ignoreRest: false, ignoreConsent: false, autoApproveDevice: false, autoConfirmRollCall: false };
export const DEV_FLAG_LABEL: Record<keyof DevFlags, string> = { ignoreRest: '休息 9 時間の確認を解除', ignoreConsent: '同意の確認を解除', autoApproveDevice: '端末を自動で承認', autoConfirmRollCall: '点呼を自動で確認（アルコール検出のときを除く）' };
export const DevUnlock = z.strictObject({ password: z.string().min(1).max(100) });
export type DevUnlock = z.infer<typeof DevUnlock>;
export const DevFlagsSave = z.strictObject({ password: z.string().min(1).max(100), flags: DevFlags });
export type DevFlagsSave = z.infer<typeof DevFlagsSave>;

export const DevSimulate = z.strictObject({ password: z.string().min(1).max(100), driverId: z.string().min(1), what: z.enum(['drive_warn', 'drive_alert', 'unknown_stop', 'no_signal']) });
export type DevSimulate = z.infer<typeof DevSimulate>;
export const DevReset = z.strictObject({ password: z.string().min(1).max(100), driverId: z.string().min(1) });
export type DevReset = z.infer<typeof DevReset>;

export const DeviceStatus = z.enum(['pending', 'approved', 'revoked']);
export type DeviceStatus = z.infer<typeof DeviceStatus>;
export const DriverDevice = z.object({ id: z.string(), status: DeviceStatus, createdAt: z.string(), approvedAt: z.string().nullable() });
export type DriverDevice = z.infer<typeof DriverDevice>;
export const PaperConsent = z.strictObject({ signedOn: z.iso.date() });
export type PaperConsent = z.infer<typeof PaperConsent>;

export const DriverDetail = z.object({
  driver: Driver,
  devices: z.array(DriverDevice),
  consentAt: z.string().nullable(),
  paperConsentOn: z.string().nullable(),
  shift: z.object({ id: z.string(), workStartAt: z.string(), startedAt: z.string().nullable(), workEndAt: z.string().nullable(), vehicle: z.string(),
    /** 位置から数えた運転・休憩・理由不明の停車（分） */
    drivingMin: z.number(), breakMin: z.number(), unknownStopMin: z.number() }).nullable(),
  rollCalls: z.array(RollCall),
  deliveries: z.array(DeliveryRow),
  /** 配送ごとの写真の枚数（積む・届けるの行き先の写真）と、問題報告（未対応・全部） */
  /** 対応前のアカウント削除の申請 */
  deleteRequest: z.object({ id: z.string(), requestedAt: z.string() }).nullable(),
  deliveryExtras: z.record(z.string(), z.object({ photos: z.number(), openIssues: z.number(), issues: z.number() })),
});
export type DriverDetail = z.infer<typeof DriverDetail>;

// ===== 記録（仕様書「運行記録の印刷」「労働時間」） =====
export const DailyReport = z.object({
  shiftId: z.string(),
  driverId: z.string(),
  vehicle: z.string(),
  workStartAt: z.string(),
  workEndAt: z.string().nullable(),
  restraintMin: z.number().nullable(),
  distanceKm: z.number(),
  drivingMin: z.number(),
  breakMin: z.number(),
  waitMin: z.number(),
  deliveries: z.number(),
  checkedBefore: z.string().nullable(),
  checkedAfter: z.string().nullable(),
});
export type DailyReport = z.infer<typeof DailyReport>;

export const WorkHours = z.object({
  driverId: z.string(),
  days: z.number(),
  restraintMin: z.number(),
  longestDayMin: z.number(),
  shortRests: z.number(),
  limitMin: z.number(),
});
export type WorkHours = z.infer<typeof WorkHours>;

// ===== 警告（仕様書「通知」の事務所（Web）） =====
export const AlertKind = z.enum(['unaccepted', 'alcohol', 'rollcall_waiting', 'due_over', 'unassigned', 'restraint', 'not_working', 'no_signal', 'issue', 'unknown_stop', 'continuous_drive', 'device_pending', 'rest_short', 'dev_mode', 'delete_request', 'chat_unread', 'route_changed', 'schedule_unassigned', 'schedule_unsent', 'schedule_driver_off', 'tomorrow_unchecked']);
export const Alert = z.object({
  id: z.string(),
  kind: AlertKind,
  level: z.enum(['warn', 'alert']),
  since: z.string(),
  /** 「対応済み」を押すまで消えない警告か */
  needsAck: z.boolean(),
  driverId: z.string().nullable(),
  deliveryNo: z.string().nullable(),
  /** 文言に使う値（名前・場所は画面が Firestore から引く）：届け先の場所、分、拘束時間（分）、点呼の ID */
  placeId: z.string().nullable(),
  minutes: z.number().nullable(),
  rollCallId: z.string().nullable(),
  dueAt: z.string().nullable(),
});
export type Alert = z.infer<typeof Alert>;
/** 対応済みにする。メモは Firestore の notes（ID は警告の ID） */
export const AlertAck = z.strictObject({});

// ===== 取り消し・付け替え・回る順番の並べ替え =====
export const Reassign = z.strictObject({ driverId: z.string().min(1, '付け替え先のドライバーを選んでください') });

/** 回る順番。訪問（同じ順番の行き先のまとまり）の今の番号を、新しい順に並べたもの */
export const Reorder = z.strictObject({ order: z.array(z.number().int().min(0)).min(1) });
export type Reorder = z.infer<typeof Reorder>;

export const BatchVisit = z.object({
  seq: z.number(),
  kind: z.enum(['pickup', 'drop']),
  placeId: z.string(),
  deliveryNos: z.array(z.string()),
  dueAt: z.string().nullable(),
  done: z.boolean(),
});
export const BatchDetail = z.object({
  batchId: z.string(),
  version: z.number(),
  driverId: z.string().nullable(),
  status: z.string(),
  visits: z.array(BatchVisit),
});
export type BatchDetail = z.infer<typeof BatchDetail>;

/** 並べ替えの決まり：同じ配送のピックアップ先は、その配送のドロップ先より前。済んだ行き先は動かせない */
export function checkOrder(visits: { seq: number; kind: 'pickup' | 'drop'; deliveryNos: string[]; done: boolean }[], order: number[]): string | null {
  const bySeq = new Map(visits.map((v) => [v.seq, v]));
  if (order.length !== visits.length || new Set(order).size !== order.length || order.some((s) => !bySeq.has(s))) return '行き先の数が合いません。画面を読み込み直してください';
  const done = visits.filter((v) => v.done).map((v) => v.seq);
  if (order.slice(0, done.length).some((s, i) => s !== done[i])) return '済んだ行き先の順番は変えられません';
  const seen = new Set<string>();
  for (const s of order) {
    const v = bySeq.get(s)!;
    if (v.kind === 'drop') for (const no of v.deliveryNos) seen.add(no);
    else for (const no of v.deliveryNos) if (seen.has(no)) return `${no} は、届ける前に積む必要があります`;
  }
  return null;
}

// ===== 位置と端末の状態（配送アプリから。仕様書「POST /locations」） =====
export const LocationPoint = z.strictObject({
  at: z.iso.datetime({ offset: true }),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracy: z.number().min(0).max(10_000).optional(),
  speedKmh: z.number().min(0).max(300).optional(),
});
export const LocationBatch = z.strictObject({
  /** 圏外の間に貯めたものをまとめて送る */
  points: z.array(LocationPoint).min(1).max(240),
  device: z.strictObject({
    battery: z.number().int().min(0).max(100).optional(),
    charging: z.boolean().optional(),
    network: z.enum(['wifi', 'cellular', 'none']).optional(),
    silent: z.boolean().optional(),
    appVersion: z.string().max(20).optional(),
  }),
});
export type LocationBatch = z.infer<typeof LocationBatch>;

// ===== ドライバーの状況（仕様書「ドライバーの状況」） =====
export const DriverState = z.enum(['off', 'rollcall_waiting', 'standby', 'unaccepted', 'to_pickup', 'to_drop', 'on_break', 'unknown_stop', 'no_signal', 'ended']);
export type DriverState = z.infer<typeof DriverState>;
export const DriverStatusRow = z.object({
  driverId: z.string(),
  color: z.string(),
  vehicle: z.string().nullable(),
  shiftId: z.string().nullable(),
  state: DriverState,
  /** その状態になった時刻（わからなければ null） */
  since: z.string().nullable(),
  next: z.object({ kind: z.enum(['pickup', 'drop']), placeId: z.string(), deliveryNos: z.array(z.string()), dueAt: z.string().nullable() }).nullable(),
  /** 今日の残りの行き先の数 */
  remaining: z.number(),
  restraintMin: z.number().nullable(),
  continuousDriveMin: z.number().nullable(),
  distanceKm: z.number(),
  position: z.object({ lat: z.number(), lng: z.number(), at: z.string() }).nullable(),
  device: z.object({ battery: z.number().nullable(), charging: z.boolean().nullable(), network: z.string().nullable(), silent: z.boolean().nullable(), appVersion: z.string().nullable(), lastSeenAt: z.string() }).nullable(),
});
export type DriverStatusRow = z.infer<typeof DriverStatusRow>;

// ===== ひな形（仕様書「ひな形に保存」「お気に入り・ひな形の管理」）。名前は Firestore の templates =====
export const TemplateCreate = z.strictObject({ stops }).superRefine((b, ctx) => checkStops(b.stops, ctx));
export type TemplateCreate = z.infer<typeof TemplateCreate>;
export const Template = z.object({ id: z.string(), stops: z.array(z.object({ kind: z.enum(['pickup', 'drop']), placeId: z.string(), dueAt: z.string().nullable(), workMin: z.number().nullable() })), createdAt: z.string() });
export type Template = z.infer<typeof Template>;

/** 場所の変更（緯度経度・お気に入り・作業時間の目安）。名前・住所などは Firestore */
export const PlaceUpdate = PlaceRegister;

// ===== 操作ログ・システムの状態（仕様書「GET /system/status」「GET /audit-logs」） =====
export const AuditLog = z.object({ id: z.string(), at: z.string(), actor: z.string(), action: z.string(), target: z.string().nullable() });
export type AuditLog = z.infer<typeof AuditLog>;
export const SystemStatus = z.object({
  lastBackupAt: z.string().nullable(),
  writesToday: z.number(),
  openShifts: z.number(),
  silentDevices: z.number(),
  appVersions: z.array(z.object({ version: z.string(), count: z.number() })),
  apiVersion: z.string(),
});
export type SystemStatus = z.infer<typeof SystemStatus>;

// ===== 人の変更と退職（仕様書「人の追加と退職」） =====
/** ドライバーの変更（D1 の部分。名前・電話・メールは Firestore） */
export const DriverUpdate = DriverCreate.pick({ accountType: true, vehicleId: true, color: true });
export const StaffMember = z.object({ id: z.string(), role: z.enum(ROLES), status: z.enum(['invited', 'active', 'retired']), retiredAt: z.string().nullable(), version: z.number() });
export type StaffMember = z.infer<typeof StaffMember>;
export const StaffUpdate = z.strictObject({ role: z.enum(ROLES) });
const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '「2026-11-02」の形で入れてください');
/** 退職の手続き：最終出勤日（この日の翌日からログインできない） */
export const Retire = z.strictObject({ lastDay: ymd });
export type Retire = z.infer<typeof Retire>;

// ===== 配送アプリの入口（/v1/driver）。返すのは自分の勤務・配送だけ =====
export const DriverRollCallSubmit = z.strictObject({
  kind: z.enum(['before', 'after']),
  vehicleId: z.string().min(1, '車両を選んでください'),
  detectorId: z.string().min(1).optional(),
  /** 測定値そのものは Firestore の rollCallDetails。ここには酒気帯びの有無だけ */
  alcoholDetected: z.boolean(),
});
export type DriverRollCallSubmit = z.infer<typeof DriverRollCallSubmit>;

export const DriverMe = z.object({
  driverId: z.string(),
  officeId: z.string(),
  shift: z.object({ id: z.string(), workStartAt: z.string(), startedAt: z.string().nullable(), vehicleId: z.string() }).nullable(),
  /** いちばん新しい終わった勤務（勤務終了のまとめ） */
  lastShift: z.object({ workEndAt: z.string(), restraintMin: z.number(), distanceKm: z.number(), deliveries: z.number(), nextAvailableAt: z.string() }).nullable(),
  /** いま走っている勤務のまとめ（乗務後の点呼の前に見せる） */
  today: z.object({ restraintMin: z.number(), distanceKm: z.number(), deliveries: z.number() }).nullable(),
  /** この端末の承認の状態 */
  device: DeviceStatus,
  /** 事務所で ON / OFF できる機能（チャット・回る順番のおすすめ・順番の変更） */
  features: z.object({ chat: z.boolean(), routeRecommend: z.boolean(), driverReorder: z.boolean() }),
  /** 依頼を受けるまでの分数（事務所の設定。受付の画面のカウントダウンに使う） */
  acceptMin: z.number(),
  /** 写真がないと完了・点呼を送れないか（保存先がある環境だけ） */
  photoRequired: z.boolean(),
  /** いまの版の同意書に同意しているか */
  consentOk: z.boolean(),
  /** 連続運転の時間（秒） */
  continuousDriveSec: z.number(),
  /** 理由を聞く停車 */
  openStop: z.object({ id: z.string(), startedAt: z.string() }).nullable(),
  /** 休憩中なら始めた時刻 */
  breakStartedAt: z.string().nullable(),
  /** いちばん新しい点呼 */
  rollCall: z.object({ id: z.string(), kind: z.enum(['before', 'after']), status: RollCallStatus, at: z.string(), alcoholDetected: z.boolean() }).nullable(),
});
export type DriverMe = z.infer<typeof DriverMe>;
/** アカウント削除の申請（配送アプリの設定から）。事務所が確認して退職の手続きなどをする */
export const DeleteRequestState = z.object({ requestedAt: z.string().nullable() });
export type DeleteRequestState = z.infer<typeof DeleteRequestState>;

// ===== 休憩・問題報告（仕様書「問題を報告」） =====
export const DriverConsent = z.strictObject({ docVersion: z.number().int().positive() });
export type DriverConsent = z.infer<typeof DriverConsent>;
export const StopReason = z.enum(['jam', 'wait', 'toilet', 'break', 'other']);
export type StopReason = z.infer<typeof StopReason>;
export const STOP_REASON_LABEL: Record<StopReason, string> = { jam: '渋滞', wait: '荷待ち', toilet: 'トイレ', break: '休憩', other: 'その他' };
export const DriverStopReason = z.strictObject({ reason: StopReason });
export type DriverStopReason = z.infer<typeof DriverStopReason>;
/** 圏外で貯めた操作は、操作した時刻を一緒に送る（仕様書「時刻」） */
export const DriverAction = z.strictObject({ clientAt: z.iso.datetime({ offset: true }).optional() });
export type DriverAction = z.infer<typeof DriverAction>;
export const DriverWait = z.strictObject({ action: z.enum(['start', 'end']), clientAt: z.iso.datetime({ offset: true }).optional() });
export type DriverWait = z.infer<typeof DriverWait>;
export const DriverAcceptAll = z.strictObject({ batchIds: z.array(z.string().min(1)).min(1).max(20) });
export type DriverAcceptAll = z.infer<typeof DriverAcceptAll>;
// ===== 写真（仕様書「写真の暗号化」「photos」） =====
export const PhotoUpload = z.strictObject({
  target: z.enum(['visit', 'rollcall', 'issue']),
  batchId: z.string().optional(),
  seq: z.coerce.number().int().min(0).optional(),
  rollCallId: z.string().optional(),
  issueId: z.string().optional(),
  /** 使い捨ての公開鍵（base64） */
  key: z.string().length(44),
  takenAt: z.iso.datetime({ offset: true }),
});
export type PhotoUpload = z.infer<typeof PhotoUpload>;
export const PhotoInfo = z.object({ id: z.string(), kind: z.enum(['pickup', 'drop', 'alcohol', 'issue']), takenAt: z.string() });
export type PhotoInfo = z.infer<typeof PhotoInfo>;
export const DriverBreak = z.strictObject({ action: z.enum(['start', 'end']) });
export type DriverBreak = z.infer<typeof DriverBreak>;
export const IssueKind = z.enum(['missing', 'absent', 'refused', 'damaged', 'vehicle', 'other']);
export type IssueKind = z.infer<typeof IssueKind>;
export const ISSUE_KIND_LABEL: Record<IssueKind, string> = { missing: '荷物がない', absent: '相手不在', refused: '受け取り拒否', damaged: '破損', vehicle: '車両トラブル', other: 'その他' };
export const IssueResolution = z.enum(['cancel', 'return', 'redeliver', 'reassign', 'continue']);
export type IssueResolution = z.infer<typeof IssueResolution>;
export const ISSUE_RESOLUTION_LABEL: Record<IssueResolution, string> = { cancel: '取り消し', return: '持ち戻り・返却', redeliver: '再配達', reassign: '付け替え', continue: 'そのまま続行' };
/** メモは Firestore の notes（ID は問題報告の ID）。写真は R2 をつないでから足す */
export const DriverIssueCreate = z.strictObject({ deliveryNo: z.string().min(1).max(20), kind: IssueKind, lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) });
export type DriverIssueCreate = z.infer<typeof DriverIssueCreate>;
export const Issue = z.object({ id: z.string(), deliveryNo: z.string(), driverId: z.string(), kind: IssueKind, lat: z.number(), lng: z.number(), reportedAt: z.string(), resolution: IssueResolution.nullable(), resolvedAt: z.string().nullable(), photos: z.number() });
export type Issue = z.infer<typeof Issue>;
export const IssueResolve = z.strictObject({ resolution: IssueResolution });
export type IssueResolve = z.infer<typeof IssueResolve>;

export const DriverVisit = z.object({
  seq: z.number(),
  /** 入力したときの順（並べ替えても変わらない。行き先ごとのメモ notes/{batchId}_{inputSeq} の ID に使う） */
  inputSeq: z.number(),
  kind: z.enum(['pickup', 'drop']),
  placeId: z.string(),
  /** ナビに渡す位置（名前・住所は Firestore の places） */
  lat: z.number(),
  lng: z.number(),
  deliveryNos: z.array(z.string()),
  dueAt: z.string().nullable(),
  workMin: z.number(),
  arrivedAt: z.string().nullable(),
  waitStartedAt: z.string().nullable(),
  waitMin: z.number(),
  /** 送った写真の枚数 */
  photos: z.number(),
  done: z.boolean(),
});
export type DriverVisit = z.infer<typeof DriverVisit>;
/** 確認のボタンを押すまで消えないお知らせ（仕様書「画面の作り」） */
export const DriverNotice = z.object({ deliveryNo: z.string(), kind: z.enum(['canceled', 'reassigned']) });
export type DriverNotice = z.infer<typeof DriverNotice>;
export const DriverNoticeAck = z.strictObject({ deliveryNos: z.array(z.string().min(1).max(20)).min(1).max(20) });
export type DriverNoticeAck = z.infer<typeof DriverNoticeAck>;
/** scheduleId・routineId：定期便・予約から送った便のとき（事務所のメモ notes/sch_{予定}・notes/rtn_{定期便} を引くため） */
export const DriverBatch = z.object({ batchId: z.string(), status: z.string(), sentAt: z.string().nullable(), scheduleId: z.string().nullable(), routineId: z.string().nullable(), visits: z.array(DriverVisit) });
export type DriverBatch = z.infer<typeof DriverBatch>;

// ===== チャット（仕様書「事務所とのチャット」）。本文は Firestore の messages、API は印（既読など）だけ =====
/** 定型文の初めの一覧（本人・事務所が直すまで使う。仕様書「事務所とのチャット」の「定型文」） */
export const MESSAGE_TEMPLATES = ['了解しました', '向かっています', '到着しました', '少し遅れます', '折り返し電話ください'] as const;
export const OFFICE_MESSAGE_TEMPLATES = ['了解しました', 'お疲れさまです', '折り返し電話します', '着いたら連絡ください', '気をつけて向かってください'] as const;
/** 定型文の一覧を整える：前後の空白を取り、空・重複を除き、長さと個数を確かめる。問題があれば文で返す */
export function cleanPhrases(items: readonly string[]): { items: string[] } | { error: string } {
  const out: string[] = [];
  for (const raw of items) {
    const t = raw.trim();
    if (!t || out.includes(t)) continue;
    if (t.length > RULES.phraseMax) return { error: `定型文は 1 つ ${RULES.phraseMax} 文字までです` };
    out.push(t);
  }
  if (out.length > RULES.phrasesMax) return { error: `定型文は ${RULES.phrasesMax} 個までです` };
  return { items: out };
}
const MessageId = z.string().regex(/^msg_[0-9a-f]{32}$/, 'メッセージの ID の形が違います');
const MessageDeliveryNo = z.string().regex(/^[0-9A-Z]{6}$/).nullable().optional();
/** 圏外のときは端末に貯めて送り直す（clientAt は端末の時刻。使わない） */
export const DriverMessageSend = z.strictObject({ id: MessageId, kind: z.enum(['text', 'template']), deliveryNo: MessageDeliveryNo, clientAt: z.string().max(40).optional() });
export type DriverMessageSend = z.infer<typeof DriverMessageSend>;
export const OfficeMessageSend = z.strictObject({ id: MessageId, driverId: z.string().min(1).max(64), kind: z.enum(['text', 'template']), urgent: z.boolean(), deliveryNo: MessageDeliveryNo });
export type OfficeMessageSend = z.infer<typeof OfficeMessageSend>;
/** 一斉連絡：宛先のドライバーごとに 1 通ずつ（本文は Firestore に 1 通ずつ置く） */
export const OfficeBroadcast = z.strictObject({
  urgent: z.boolean(),
  items: z.array(z.strictObject({ id: MessageId, driverId: z.string().min(1).max(64) })).min(1, '宛先がいません').max(200),
});
export type OfficeBroadcast = z.infer<typeof OfficeBroadcast>;
export const OfficeMessageRead = z.strictObject({ driverId: z.string().min(1).max(64) });
export type OfficeMessageRead = z.infer<typeof OfficeMessageRead>;
/** 既読の印。readBy はスタッフの ID（ドライバーが読んだときはドライバーの ID） */
export const MessageMark = z.object({ id: z.string(), from: z.enum(['driver', 'staff']), readAt: z.string().nullable(), readBy: z.string().nullable() });
export type MessageMark = z.infer<typeof MessageMark>;
/** 事務所のチャットの一覧：ドライバーごとの未読と最後のやり取り */
export const ChatThread = z.object({ driverId: z.string(), unread: z.number(), lastAt: z.string().nullable() });
export type ChatThread = z.infer<typeof ChatThread>;
export const DriverChatState = z.object({ unread: z.number(), marks: z.array(MessageMark) });
export type DriverChatState = z.infer<typeof DriverChatState>;
export const newMessageId = () => {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return `msg_${[...b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

// ===== 回る順番のおすすめ（仕様書「回る順番のおすすめ」） =====
export const RouteSummary = z.object({
  /** 行き先の番号の並び（配送指示なら visit の seq、入力中なら入力の並びの番号） */
  order: z.array(z.number()),
  pattern: z.string(),
  totalMin: z.number(),
  driveMin: z.number(),
  lateMin: z.number(),
  etas: z.array(z.object({ key: z.number(), arriveAt: z.string(), lateMin: z.number() })),
});
export type RouteSummary = z.infer<typeof RouteSummary>;
export const RoutePlan = z.object({
  current: RouteSummary,
  recommended: RouteSummary,
  /** おすすめにすると短くなる分（遅れの重みを含む）。0 なら今の順のまま */
  gainMin: z.number(),
  /** 速さを事務所の走行の記録から出したか（足りなければ決まりの値） */
  learned: z.boolean(),
  /** 今いる場所から数えたか（位置がなければ最初の行き先から） */
  fromHere: z.boolean(),
});
export type RoutePlan = z.infer<typeof RoutePlan>;
/** 入力中の配送指示のおすすめと、勤務中のドライバーの候補（足したら何分増えるか） */
export const DraftPlanRequest = z.strictObject({ stops: z.array(StopInput).min(2).max(RULES.maxStopsPerBatch) });
export type DraftPlanRequest = z.infer<typeof DraftPlanRequest>;
export const DriverCandidate = z.object({ driverId: z.string(), addMin: z.number(), lateMin: z.number(), restraintMin: z.number().nullable(), overRestraint: z.boolean() });
export type DriverCandidate = z.infer<typeof DriverCandidate>;
export const DraftPlan = z.object({ plan: RoutePlan, candidates: z.array(DriverCandidate) });
export type DraftPlan = z.infer<typeof DraftPlan>;
export const DriverReorder = z.strictObject({ order: z.array(z.number().int().min(0)).min(1).max(48), clientAt: z.string().max(40).optional() });
export type DriverReorder = z.infer<typeof DriverReorder>;

// ===== 定期便・月間スケジュール（仕様書「定期便の登録」「月間スケジュール」） =====
/** くり返し：毎日／曜日（0 = 日曜）／毎月の日付（32 = 月末） */
export const RoutineRepeat = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('daily') }),
  z.strictObject({ type: z.literal('weekly'), days: z.array(z.number().int().min(0).max(6)).min(1, '曜日を選んでください').max(7) }),
  z.strictObject({ type: z.literal('monthly'), days: z.array(z.number().int().min(1).max(32)).min(1, '日付を選んでください').max(31) }),
]);
export type RoutineRepeat = z.infer<typeof RoutineRepeat>;
export const RoutineInput = z.strictObject({
  templateId: z.string().min(1, 'ひな形を選んでください'),
  repeat: RoutineRepeat,
  startDate: ymd,
  endDate: ymd.nullable(),
  sendTime: hhmm,
  driverId: z.string().min(1).nullable(),
  runOnHolidays: z.boolean(),
}).refine((r) => !r.endDate || r.endDate >= r.startDate, { message: '終わりの日は始まりの日より後にしてください', path: ['endDate'] });
export type RoutineInput = z.infer<typeof RoutineInput>;
export const Routine = z.object({
  id: z.string(), templateId: z.string(), repeat: RoutineRepeat, startDate: z.string(), endDate: z.string().nullable(), sendTime: z.string(),
  driverId: z.string().nullable(), runOnHolidays: z.boolean(), status: z.enum(['active', 'stopped']), version: z.number(),
});
export type Routine = z.infer<typeof Routine>;
export const ScheduleItem = z.object({
  id: z.string(), date: z.string(), sendTime: z.string(), routineId: z.string().nullable(), templateId: z.string(), driverId: z.string().nullable(),
  status: z.enum(['planned', 'skipped', 'sent', 'canceled']), batchId: z.string().nullable(), version: z.number(),
  /** 行き先の並び（P・D） */
  pattern: z.string(),
  /** 前日の「明日の予定の確認」をした時刻 */
  checkedAt: z.string().nullable(),
  checkedBy: z.string().nullable(),
  /** 担当が休みの日 */
  driverOff: z.boolean(),
  /** 担当が送る時刻に勤務を始められない見込み（休息 9 時間未満） */
  restShort: z.boolean(),
});
export type ScheduleItem = z.infer<typeof ScheduleItem>;
/** その日だけの変更：休みにする・戻す、担当・時刻を変える */
export const ScheduleUpdate = z.strictObject({ status: z.enum(['planned', 'skipped']), driverId: z.string().min(1).nullable(), sendTime: hhmm });
export type ScheduleUpdate = z.infer<typeof ScheduleUpdate>;

/** その日がくり返しに当たるか（ymd は YYYY-MM-DD。曜日・月末は暦で数える） */
export function repeatsOn(r: RoutineRepeat, ymdStr: string) {
  const d = new Date(`${ymdStr}T00:00:00Z`);
  if (r.type === 'daily') return true;
  if (r.type === 'weekly') return r.days.includes(d.getUTCDay());
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  return r.days.includes(d.getUTCDate()) || (r.days.includes(32) && d.getUTCDate() === last);
}
/** from から to まで（両端を含む）の日付の一覧 */
export function daysBetween(from: string, to: string) {
  const out: string[] = [];
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`); t += 86_400_000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}
export const addDays = (ymdStr: string, n: number) => new Date(Date.parse(`${ymdStr}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** 先の日付のスポット便の予約（配送指示の入力と同じ行き先＋日付・時刻。仕様書「月間スケジュール」） */
export const SpotReserve = z.strictObject({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), sendTime: hhmm, driverId: z.string().min(1).nullable(), stops })
  .superRefine((b, ctx) => checkStops(b.stops, ctx));
export type SpotReserve = z.infer<typeof SpotReserve>;
export const ScheduleCheck = z.strictObject({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) });
export const DayOffInput = z.strictObject({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日付を選んでください') });

// ===== 運行表（仕様書「運行表（タイムライン）」） =====
const Band = z.object({ start: z.string(), end: z.string().nullable() });
export const TimelineVisit = z.object({
  batchId: z.string(), seq: z.number(), kind: z.enum(['pickup', 'drop']), placeId: z.string(), deliveryNos: z.array(z.string()), dueAt: z.string().nullable(),
  /** 配送の荷主（表の絞り込み用。荷主の欄が空の配送は入らない） */
  clientIds: z.array(z.string()),
  /** 済んだ・作業中は到着の時刻、これからは到着見込み（今いる場所から今の順で数える）。scheduled は送る前の予定（定期便・予約）で、送る時刻から数えた目安 */
  at: z.string().nullable(), doneAt: z.string().nullable(), workMin: z.number(),
  state: z.enum(['done', 'arrived', 'pending', 'scheduled']),
  /** 予定の到着（受けたときの見込み。受ける前・記録がない便は null） */
  plannedAt: z.string().nullable(),
  /** over：必着を過ぎてまだ / late：必着に間に合わない見込み / behind：予定より 15 分以上遅れ */
  warn: z.enum(['over', 'late', 'behind']).nullable(),
});
export type TimelineVisit = z.infer<typeof TimelineVisit>;
export const TimelineRow = z.object({
  driverId: z.string(),
  shift: Band.nullable(),
  breaks: z.array(Band),
  /** 停車（unknown は理由不明） */
  stops: z.array(Band.extend({ unknown: z.boolean() })),
  visits: z.array(TimelineVisit),
  /** その日の定期便・予約（まだ送っていないもの） */
  schedules: z.array(z.object({ id: z.string(), sendTime: z.string(), routineId: z.string().nullable(), status: z.enum(['planned', 'skipped', 'sent', 'canceled']) })),
});
export type TimelineRow = z.infer<typeof TimelineRow>;
export const Timeline = z.object({
  date: z.string(),
  rows: z.array(TimelineRow),
  /** 未割り当ての配送（いちばん上の行）と、担当未定の予定 */
  unassigned: z.array(z.object({ batchId: z.string(), deliveryNos: z.array(z.string()), createdAt: z.string(), dropPlaceIds: z.array(z.string()) })),
  unassignedSchedules: z.array(z.object({ id: z.string(), sendTime: z.string(), routineId: z.string().nullable() })),
});
export type Timeline = z.infer<typeof Timeline>;

// ===== 編集ロック（仕様書「同時編集の防止」） =====
/** driver:{ドライバー}:{日付}・routine:{定期便}・client:{荷主}・driverinfo:{ドライバー} */
export const LockKey = z.string().regex(/^(driver:[\w-]{1,64}:\d{4}-\d{2}-\d{2}|routine:[\w-]{1,64}|client:[\w-]{1,64}|driverinfo:[\w-]{1,64})$/, 'ロックの対象の形が違います');
export const LockRequest = z.strictObject({ key: LockKey });
export const LockInfo = z.object({ key: z.string(), by: z.string(), expiresAt: z.string() });
export type LockInfo = z.infer<typeof LockInfo>;
export const lockKeys = {
  driverDay: (driverId: string, date: string) => `driver:${driverId}:${date}`,
  routine: (id: string) => `routine:${id}`,
  client: (id: string) => `client:${id}`,
  driverInfo: (id: string) => `driverinfo:${id}`,
};

// ===== 走行データ（仕様書「走行データ」） =====
export const DrivingDay = z.object({
  date: z.string(),
  distanceKm: z.number(), drivingMin: z.number(), workMin: z.number(), breakMin: z.number(), unknownStopMin: z.number(), restraintMin: z.number(),
  /** 連続運転の最長（期間の合計では最も長い日の値） */
  maxContinuousMin: z.number(),
  deliveries: z.number(),
  /** 必着のある届け先の数と、間に合った数 */
  dueTotal: z.number(), dueOnTime: z.number(),
});
export type DrivingDay = z.infer<typeof DrivingDay>;
export const DrivingReport = z.object({
  from: z.string(), to: z.string(),
  days: z.array(DrivingDay),
  totals: DrivingDay.omit({ date: true }),
  /** 事務所全体の 1 日あたりの平均（勤務した日で割る） */
  officeAvg: DrivingDay.omit({ date: true }),
  /** 1 日だけを見たときの速さ（スマホの GPS の目安。速度違反の証拠にはしない） */
  speed: z.object({ maxKmh: z.number().nullable(), avgKmh: z.number().nullable(), fast: z.array(z.object({ start: z.string(), end: z.string(), maxKmh: z.number() })) }).nullable(),
});
export type DrivingReport = z.infer<typeof DrivingReport>;
